import type { ToolId } from '../core/types';
import { cropTool } from './crop';
import { gradientTool } from './gradient';
import { bucketTool, eyedropperTool, handTool, zoomTool } from './misc';
import {
  blurTool,
  brushTool,
  burnTool,
  cloneTool,
  dodgeTool,
  eraserTool,
  healingTool,
  pencilTool,
  sharpenTool,
  smudgeTool,
  spongeTool,
  spotHealingTool,
} from './paint';
import { patchTool } from './patch';
import {
  lassoTool,
  magicWandTool,
  magneticLassoTool,
  polygonLassoTool,
  quickSelectTool,
  selectEllipseTool,
  selectRectTool,
} from './selection';
import { typeTool } from './text';
import { moveTool, transformTool } from './transform';
import type { Tool } from './types';
import {
  directSelectTool,
  freePenTool,
  pathSelectTool,
  penTool,
  shapeEllipseTool,
  shapeLineTool,
  shapePolygonTool,
  shapeRectTool,
} from './vector';

export const TOOLS: Record<ToolId, Tool> = {
  move: moveTool,
  selectRect: selectRectTool,
  selectEllipse: selectEllipseTool,
  lasso: lassoTool,
  lassoPolygon: polygonLassoTool,
  lassoMagnetic: magneticLassoTool,
  magicWand: magicWandTool,
  quickSelect: quickSelectTool,
  crop: cropTool,
  transform: transformTool,
  brush: brushTool,
  pencil: pencilTool,
  eraser: eraserTool,
  clone: cloneTool,
  spotHealing: spotHealingTool,
  healing: healingTool,
  patch: patchTool,
  blur: blurTool,
  sharpen: sharpenTool,
  smudge: smudgeTool,
  dodge: dodgeTool,
  burn: burnTool,
  sponge: spongeTool,
  gradient: gradientTool,
  bucket: bucketTool,
  pen: penTool,
  penFree: freePenTool,
  pathSelect: pathSelectTool,
  directSelect: directSelectTool,
  shapeRect: shapeRectTool,
  shapeEllipse: shapeEllipseTool,
  shapeLine: shapeLineTool,
  shapePolygon: shapePolygonTool,
  type: typeTool,
  eyedropper: eyedropperTool,
  hand: handTool,
  zoom: zoomTool,
};

export interface ToolDescriptor {
  id: ToolId;
  label: string;
  /**
   * Photoshop tool letter. Tools that share a letter form a cycle reachable with
   * `Shift+letter`. Empty for tools that are only available through a chord.
   */
  key: string;
  /** Display form for tooltips, e.g. `Shift+M` or `Ctrl+T`. */
  shortcut: string;
  /** Inline SVG path data drawn in a 24x24 viewBox. */
  icon: string;
}

/**
 * Toolbar layout: sections → slots → tools in a Photoshop-style flyout.
 * A slot with more than one tool shows a corner triangle; click-and-hold or
 * right-click opens the nest.
 */
export const TOOLBAR_SECTIONS: ToolDescriptor[][][] = [
  [
    [
      {
        id: 'move',
        label: 'Move',
        key: 'v',
        shortcut: 'V',
        icon: 'M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3',
      },
      {
        id: 'transform',
        label: 'Free Transform',
        key: '',
        shortcut: 'Ctrl+T',
        icon: 'M5 5h14v14H5zM5 5l14 14M2 2h3v3H2zM19 2h3v3h-3zM2 19h3v3H2zM19 19h3v3h-3z',
      },
    ],
  ],
  [
    [
      {
        id: 'selectRect',
        label: 'Rectangular Marquee',
        key: 'm',
        shortcut: 'M',
        icon: 'M3 5h18v14H3z',
      },
      {
        id: 'selectEllipse',
        label: 'Elliptical Marquee',
        key: 'm',
        shortcut: 'Shift+M',
        icon: 'M12 5c5 0 9 3.1 9 7s-4 7-9 7-9-3.1-9-7 4-7 9-7z',
      },
    ],
    [
      {
        id: 'lasso',
        label: 'Lasso',
        key: 'l',
        shortcut: 'L',
        icon: 'M4 13c0-4.4 3.6-8 8-8s8 3.1 8 7c0 3.3-3 5-6 5-2 0-3 1-3 2.5S12 22 10 22s-3-1.4-3-3 1-2.6 1-2.6C5.8 15.6 4 14.6 4 13z',
      },
      {
        id: 'lassoPolygon',
        label: 'Polygonal Lasso',
        key: 'l',
        shortcut: 'Shift+L',
        icon: 'M4 9l7-5 9 4-2 9-8 3-6-5zM4 9l6 6M11 4l1 11M20 8l-8 7',
      },
      {
        id: 'lassoMagnetic',
        label: 'Magnetic Lasso',
        key: 'l',
        shortcut: 'Shift+L',
        icon: 'M5 4v7a7 7 0 0014 0V4h-4v7a3 3 0 01-6 0V4zM5 8h4M15 8h4',
      },
    ],
    [
      {
        id: 'magicWand',
        label: 'Magic Wand',
        key: 'w',
        shortcut: 'W',
        icon: 'M3 21l11-11M13 4l1 3 3 1-3 1-1 3-1-3-3-1 3-1zM19 9l.7 1.8L21.5 12l-1.8.7L19 15l-.7-2.3L16.5 12l1.8-1.2z',
      },
      {
        id: 'quickSelect',
        label: 'Quick Selection',
        key: 'w',
        shortcut: 'Shift+W',
        icon: 'M4 18c3-9 8-9 11 0M9 4l.8 2.2L12 7l-2.2.8L9 10l-.8-2.2L6 7l2.2-.8zM17 3l.6 1.6L19 5l-1.4.6L17 7l-.6-1.4L15 5l1.4-.4z',
      },
    ],
    [{ id: 'crop', label: 'Crop', key: 'c', shortcut: 'C', icon: 'M6 2v16h16M2 6h16v16' }],
  ],
  [
    [
      {
        id: 'eyedropper',
        label: 'Eyedropper',
        key: 'i',
        shortcut: 'I',
        icon: 'M15 3l6 6-3 3-1-1-8 8-4 1 1-4 8-8-1-1z',
      },
    ],
    [
      {
        id: 'spotHealing',
        label: 'Spot Healing Brush',
        key: 'j',
        shortcut: 'J',
        icon: 'M12 4c3 4 5 6 5 9a5 5 0 01-10 0c0-3 2-5 5-9zM9 15h6M12 12v6',
      },
      {
        id: 'healing',
        label: 'Healing Brush',
        key: 'j',
        shortcut: 'Shift+J',
        icon: 'M5 19l4-1 9-9a2.8 2.8 0 10-4-4l-9 9zM8 15h5M10.5 12.5v5',
      },
      {
        id: 'patch',
        label: 'Patch',
        key: 'j',
        shortcut: 'Shift+J',
        icon: 'M4 8c4-4 12-4 16 0-4 4-4 8 0 12-4 4-12 4-16 0 4-4 4-8 0-12z',
      },
    ],
    [
      {
        id: 'brush',
        label: 'Brush',
        key: 'b',
        shortcut: 'B',
        icon: 'M4 20c1-4 3-5 5-6l9-9a2.8 2.8 0 014 4l-9 9c-1 2-2 4-6 5z',
      },
      {
        id: 'pencil',
        label: 'Pencil',
        key: 'b',
        shortcut: 'Shift+B',
        icon: 'M4 20l1.5-4.5L16 5l3 3-10.5 10.5zM14 7l3 3',
      },
    ],
    [
      {
        id: 'clone',
        label: 'Clone Stamp',
        key: 's',
        shortcut: 'S',
        icon: 'M9 3h6v4l3 5H6l3-5zM7 12h10v3H7zM10 15v6h4v-6',
      },
    ],
    [
      {
        id: 'eraser',
        label: 'Eraser',
        key: 'e',
        shortcut: 'E',
        icon: 'M8 20h12M4 16L14 6l6 6-8 8H7z',
      },
    ],
    [
      {
        id: 'gradient',
        label: 'Gradient',
        key: 'g',
        shortcut: 'G',
        icon: 'M3 5h18v14H3zM6 17V7M9 17V7M12 17V7M15 17V7M18 17V7',
      },
      {
        id: 'bucket',
        label: 'Paint Bucket',
        key: 'g',
        shortcut: 'Shift+G',
        icon: 'M5 12l7-7 8 8-7 7zM3 20c0-2 2-4 2-4s2 2 2 4a2 2 0 11-4 0z',
      },
    ],
  ],
  [
    [
      {
        id: 'blur',
        label: 'Blur',
        key: 'r',
        shortcut: 'R',
        icon: 'M12 3c4 5 6 7 6 10a6 6 0 01-12 0c0-3 2-5 6-10zM8 14h8M9 17h6',
      },
      {
        id: 'sharpen',
        label: 'Sharpen',
        key: 'r',
        shortcut: 'Shift+R',
        icon: 'M12 3l7 16H5zM12 9v6',
      },
      {
        id: 'smudge',
        label: 'Smudge',
        key: 'r',
        shortcut: 'Shift+R',
        icon: 'M6 20c0-4 2-6 5-8 2-1.4 3-3 3-5a3 3 0 00-6 0M14 20c3-1 6-3 6-6',
      },
    ],
    [
      {
        id: 'dodge',
        label: 'Dodge',
        key: 'o',
        shortcut: 'O',
        icon: 'M12 7a5 5 0 100 10 5 5 0 000-10zM12 2v3M12 19v3M2 12h3M19 12h3',
      },
      {
        id: 'burn',
        label: 'Burn',
        key: 'o',
        shortcut: 'Shift+O',
        icon: 'M13 2c1 5-4 6-4 11a4 4 0 008 0c0-2-1-3-1-5 3 2 4 4 4 7a7 7 0 01-14 0C6 9 11 6 13 2z',
      },
      {
        id: 'sponge',
        label: 'Sponge',
        key: 'o',
        shortcut: 'Shift+O',
        icon: 'M4 12a8 5 0 0116 0v4a8 5 0 01-16 0zM8 11v2M12 10v3M16 11v2',
      },
    ],
  ],
  [
    [
      {
        id: 'type',
        label: 'Horizontal Type',
        key: 't',
        shortcut: 'T',
        icon: 'M5 6h14M12 6v14M8 20h8',
      },
    ],
  ],
  [
    [
      {
        id: 'pen',
        label: 'Pen',
        key: 'p',
        shortcut: 'P',
        icon: 'M4 20l2-6 9-9 4 4-9 9zM14 6l4 4M4 20l4-1',
      },
      {
        id: 'penFree',
        label: 'Free Pen',
        key: 'p',
        shortcut: 'Shift+P',
        icon: 'M3 17c4-10 8 4 12-3 1.6-2.8 4-2 6-1M18 3l3 3-9 9-4 1 1-4z',
      },
    ],
    [
      {
        id: 'pathSelect',
        label: 'Path Selection',
        key: 'a',
        shortcut: 'A',
        icon: 'M6 3l12 8-5 1.6L11 21zM3 3h3v3H3z',
      },
      {
        id: 'directSelect',
        label: 'Direct Selection',
        key: 'a',
        shortcut: 'Shift+A',
        icon: 'M7 4l10 7-4 1.4-1.6 4.6zM3 18h3v3H3zM18 3h3v3h-3z',
      },
    ],
    [
      {
        id: 'shapeRect',
        label: 'Rectangle',
        key: 'u',
        shortcut: 'U',
        icon: 'M3 6h18v12H3z',
      },
      {
        id: 'shapeEllipse',
        label: 'Ellipse',
        key: 'u',
        shortcut: 'Shift+U',
        icon: 'M12 5a9 7 0 100 14 9 7 0 000-14z',
      },
      {
        id: 'shapeLine',
        label: 'Line',
        key: 'u',
        shortcut: 'Shift+U',
        icon: 'M4 20L20 4M3 19h3v3H3zM18 2h3v3h-3z',
      },
      {
        id: 'shapePolygon',
        label: 'Polygon',
        key: 'u',
        shortcut: 'Shift+U',
        icon: 'M12 3l8 6-3 10H7L4 9z',
      },
    ],
  ],
  [
    [
      {
        id: 'hand',
        label: 'Hand',
        key: 'h',
        shortcut: 'H',
        icon: 'M8 13V5a2 2 0 114 0v6m0-2a2 2 0 114 0v3m0-1a2 2 0 114 0v5a6 6 0 01-6 6h-3a6 6 0 01-6-6v-3a2 2 0 114 0',
      },
    ],
    [
      {
        id: 'zoom',
        label: 'Zoom',
        key: 'z',
        shortcut: 'Z',
        icon: 'M10 3a7 7 0 105.2 11.7L21 21M7 10h6M10 7v6',
      },
    ],
  ],
];

/** Flat list of every tool descriptor, in toolbar order. */
export const TOOL_CATALOG: ToolDescriptor[] = TOOLBAR_SECTIONS.flat(2);

/** @deprecated Prefer TOOL_CATALOG — kept for older call sites that expect a flat list. */
export const TOOL_GROUPS: ToolDescriptor[] = TOOL_CATALOG;

/**
 * Tools grouped by their Photoshop letter, in toolbar order. A plain letter
 * selects the tool last used in the group; `Shift+letter` advances the cycle.
 */
export const TOOL_CYCLES: { key: string; tools: ToolId[] }[] = (() => {
  const cycles: { key: string; tools: ToolId[] }[] = [];
  for (const descriptor of TOOL_CATALOG) {
    if (!descriptor.key) continue;
    const existing = cycles.find((cycle) => cycle.key === descriptor.key);
    if (existing) existing.tools.push(descriptor.id);
    else cycles.push({ key: descriptor.key, tools: [descriptor.id] });
  }
  return cycles;
})();

export const TOOL_LABELS: Record<ToolId, string> = Object.fromEntries(
  TOOL_CATALOG.map((tool) => [tool.id, tool.label]),
) as Record<ToolId, string>;

export type { Tool } from './types';
